import { useQuery } from '@tanstack/react-query'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useAnalyticsScope } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { useCountries } from '@/application/queries'
import { useFilter } from '@/application/use-filter'
import { toCountryCodes } from '@/domain/countries'
import { DashboardAnalyticsApiReturnRateTableRequest } from '@itsrever/rever-api'

function getQueryKey(
  ecommerceIds: string[],
  startDate?: string,
  endDate?: string,
  returnRateTableArgs?: DashboardAnalyticsApiReturnRateTableRequest,
  countries?: string[],
  collections?: string[]
) {
  return [
    'productsReturnRate',
    ecommerceIds,
    startDate,
    endDate,
    returnRateTableArgs,
    countries,
    collections
  ]
}

export function useProductsReturnRate(startDate?: string, endDate?: string) {
  const { ecommerceIds } = useAnalyticsScope()
  const { countries, loadingCountries } = useCountries()

  const { getFilterValues: getSelectedCountries } = useFilter('countries')
  const selectedCountries = getSelectedCountries()
  const { getFilterValues: getSelectedCollections } = useFilter('collections')
  const selectedCollections = getSelectedCollections()

  const returnRateTableArgs = useAppSelector(
    (store) => store.analyticsApi.returnRateTableArgs
  )

  async function callProductsReturnRate() {
    if (!ecommerceIds.length) return
    return AnalyticsApi.getProductsReturnRate({
      ...returnRateTableArgs,
      ecommerceId: ecommerceIds[0],
      ecommerceIds,
      startDate,
      endDate,
      pageLength: 20,
      countries: toCountryCodes(selectedCountries, countries),
      collections: selectedCollections
    })
  }

  const productsReturnRateQuery = useQuery({
    queryKey: getQueryKey(
      ecommerceIds,
      startDate,
      endDate,
      returnRateTableArgs,
      selectedCountries,
      selectedCollections
    ),
    queryFn: callProductsReturnRate,
    staleTime: 1000 * 60 * 15,
    enabled:
      ecommerceIds.length > 0 && !!startDate && !!endDate && !!countries.length
  })

  const productsReturnRate =
    productsReturnRateQuery.data?.productsReturnRate ?? []
  const totalEntries = productsReturnRateQuery.data?.totalEntries ?? 0

  return {
    productsReturnRate,
    totalEntries,
    loading: productsReturnRateQuery.isLoading || loadingCountries
  }
}
